<!--
  A submitted review's inline comments inside its conversation entry,
  folded until the reader unfolds them (remembered per review in the PR's
  prefs). Unfolded, each thread the review commented in shows where it is,
  whether it is resolved or outdated, the diff lines it was written on, the
  review's comments and the replies that followed, and a link that opens
  it in the Files view.
-->
<script lang="ts">
  import type { PrStore } from '../state/pr.svelte';
  import { nav } from '../nav.svelte';
  import { relTime } from '../logic/time';
  import { parseOffDiff } from '../logic/offdiff';
  import { commentHref, reviewThreads, threadPlace } from '../logic/reviewComments';
  import { carryFilter } from '../logic/filefilter';
  import { prPath } from '../router';
  import CommentBody from './CommentBody.svelte';
  import Html from './Html.svelte';
  import HunkRows from './HunkRows.svelte';

  interface Props {
    pr: PrStore;
    /** The review's node ID. */
    review: string;
    /** How many inline comments GitHub says the review has. */
    count: number;
  }
  let { pr, review, count }: Props = $props();

  const open = $derived(!!pr.prefs.data.openReviews?.[review]);
  const entries = $derived(open ? reviewThreads(review, pr.threads) : []);
  const shown = $derived(entries.reduce((n, e) => n + e.own, 0));
  const head = $derived(pr.pr?.headRefOid ?? '');
  const ref = $derived({ owner: pr.owner, repo: pr.repo, number: pr.number });
  const bodyId = $derived(`rv-${review}`);
  const label = $derived(`${count} inline comment${count === 1 ? '' : 's'}`);
  /** The Files view, keeping the reader's file filter: comments not loaded here are there. */
  const filesHref = $derived(carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files'), nav.query));
</script>

<button type="button" class="rv-toggle" aria-expanded={open} aria-controls={open ? bodyId : undefined} onclick={() => pr.prefs.setReviewOpen(review, !open)}
  >{label}</button
>
{#if open}
  <div class="rv-body" id={bodyId}>
    {#if !entries.length}
      <p class="muted small">These comments are not loaded here. <a href={filesHref}>Open the Files view</a></p>
    {:else}
      <ol class="rv-threads" aria-label="Inline comments of this review">
        {#each entries as e (e.thread.id)}
          {@const t = e.thread}
          {@const first = e.comments[0]}
          <li class="rv-thread" class:resolved={t.isResolved}>
            <div class="rv-where">
              <a class="rv-place" href={commentHref(t, first.id, ref, head, nav.query)} title="Open this comment in the Files view">{threadPlace(t)}</a>
              {#if t.isResolved}<span class="pill">resolved{t.resolvedBy ? ` by ${t.resolvedBy.login}` : ''}</span>{/if}
              {#if t.isOutdated}<span class="pill pending">outdated</span>{/if}
              {#if e.earlier}<span class="muted small">reply in a thread {e.earlier === 1 ? 'with 1 earlier comment' : `with ${e.earlier} earlier comments`}</span>{/if}
            </div>
            <HunkRows rows={e.hunk} place={threadPlace(t)} />
            <ol class="rv-comments">
              {#each e.comments as c, k (c.id)}
                {@const anchored = k === 0 && e.earlier === 0 ? parseOffDiff(c.body) : null}
                <li class:own={c.pullRequestReview?.id === review}>
                  <div class="comment-head">
                    <b>{c.author?.login ?? 'ghost'}</b><span title={c.createdAt}>{k === 0 ? 'commented' : 'replied'} {relTime(c.createdAt)}</span>
                  </div>
                  {#if anchored}<Html md={anchored.text} />{:else}<CommentBody {pr} thread={t} comment={c} />{/if}
                </li>
              {/each}
            </ol>
            <a class="rv-open" href={commentHref(t, first.id, ref, head, nav.query)}>Open in Files <span aria-hidden="true">→</span></a>
          </li>
        {/each}
      </ol>
      {#if shown < count}
        <p class="muted small rv-more">Showing {shown} of {count}; the rest are in threads not loaded here.</p>
      {/if}
    {/if}
  </div>
{/if}


<style>
  .rv-toggle {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    border: 0;
    background: none;
    padding: 2px 0;
    color: var(--accent);
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .rv-toggle::before {
    content: '▸';
    display: inline-block;
    color: var(--muted);
    font-size: 12px;
    line-height: 1;
    transition: transform 0.15s;
  }
  .rv-toggle[aria-expanded='true']::before {
    transform: rotate(90deg);
  }
  .rv-toggle:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .rv-body {
    flex-basis: 100%;
    margin-top: 10px;
  }
  .rv-threads,
  .rv-comments {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .rv-threads {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
  .rv-thread {
    min-width: 0;
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 10px 12px;
    background: var(--bg);
  }
  .rv-where {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    align-items: center;
    font-size: 12px;
    min-width: 0;
  }
  .rv-where .pill {
    font-size: 11px;
    padding: 1px 8px;
  }
  .rv-place {
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    overflow-wrap: anywhere;
    min-width: 0;
  }
  .rv-comments {
    display: grid;
    gap: 8px;
  }
  .rv-comments > li {
    min-width: 0;
  }
  .rv-comments > li + li {
    padding-top: 8px;
    border-top: 1px dashed var(--line);
  }
  .rv-comments :global(.comment-body) {
    font-size: 14px;
  }
  .rv-thread.resolved .rv-comments {
    opacity: 0.85;
  }
  .rv-open {
    display: inline-block;
    margin-top: 6px;
    font-size: 12px;
  }
  .rv-more {
    margin-top: 8px;
  }
  @media (prefers-reduced-motion: reduce) {
    .rv-toggle::before {
      transition: none;
    }
  }
</style>
